import { useState, useEffect } from "react";
import { FaFilePdf, FaTrash, FaCheck, FaTimesCircle, FaDownload, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { FiFolder, FiHome, FiChevronLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/SocketContext";
import PageHeader from "../../../components/PageHeader";
import { useToast } from "../../../components/ui/Toast";
import { TableSkeleton } from "../../../components/ui/Skeleton";
import EmptyState from "../../../components/ui/EmptyState";
import { usePortalContent, getPortalIcon, getAccountPageMeta } from "../../../portal";

const Invoice = () => {
  const { currentThemeColors } = useTheme();
  const { content: invoiceContent } = usePortalContent('account-invoice');
  const pageMeta = getAccountPageMeta(invoiceContent, 'invoice');
  const InvoiceIcon = getPortalIcon(pageMeta.icon);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvoices = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        const invoicesReversed = (data.user.invoices || []).reverse();
        setInvoices(invoicesReversed);
        setFilteredInvoices(invoicesReversed);
        setFolders((data.user.folders || []).filter((f) => (f.section || "documents") === "invoices"));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [navigate]);

  useEffect(() => {
    const filtered = invoices.filter((invoice) => {
      const nameMatch = (invoice.originalName || "Invoice").toLowerCase().includes(searchTerm.toLowerCase());
      const folderMatch = currentFolder
        ? invoice.folderId?.toString() === currentFolder.toString()
        : !invoice.folderId;
      return nameMatch && folderMatch;
    });
    setFilteredInvoices(filtered);
  }, [searchTerm, invoices, currentFolder]);

  const getInvoiceFolderContents = () => {
    if (!currentFolder) return folders.filter((f) => !f.parentFolder);
    const currentId = currentFolder.toString();
    return folders.filter((f) => f.parentFolder?.toString() === currentId);
  };

  const openFolder = (folder) => {
    setCurrentFolder(folder._id);
    setFolderPath([...folderPath, folder]);
  };

  const navigateToFolder = (index) => {
    if (index === -1) {
      setCurrentFolder(null);
      setFolderPath([]);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[newPath.length - 1]._id);
    }
  };

  const hasInvoiceFolderNav = folders.length > 0 || currentFolder || folderPath.length > 0;

  const handleInvoiceBack = () => {
    if (folderPath.length <= 1) {
      navigateToFolder(-1);
    } else {
      navigateToFolder(folderPath.length - 2);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      // If there's no fileUrl, it means the invoice was cancelled or not prepared
      if (!invoice.fileUrl) {
        toast.warning("Invoice is not available for download");
        return;
      }

      // Open in new tab for download
      window.open(`${process.env.REACT_APP_API_BASE_URL}${invoice.fileUrl?.replace(/http:\/\/localhost:5000\//, '/')}`, '_blank');

      // Mark invoice as downloaded
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/invoice/mark-downloaded`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ invoiceId: invoice._id })
      });

      if (response.ok) {
        // Update local state to show invoice as downloaded
        setInvoices(prevInvoices => 
          prevInvoices.map(inv => 
            inv._id === invoice._id ? {...inv, downloaded: true} : inv
          )
        );
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
    }
  };

  const handleCancelInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setCancelDialogOpen(true);
  };

  const confirmCancelInvoice = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/invoice/cancel-invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          paymentId: selectedInvoice.paymentId,
          reason: cancelReason 
        })
      });

      if (response.ok) {
        // Update local state to show invoice as cancelled
        const updatedInvoice = { 
          ...selectedInvoice, 
          status: 'cancelled', 
          fileUrl: null 
        };

        // Add the cancelled invoice to the list and refresh the view
        setInvoices(prevInvoices => [...prevInvoices, updatedInvoice]);
        
        toast.success("Invoice cancelled successfully");
      } else {
        toast.error("Failed to cancel invoice");
      }
    } catch (error) {
      console.error("Error cancelling invoice:", error);
      toast.error("Error cancelling invoice");
    } finally {
      setCancelDialogOpen(false);
      setCancelReason("");
      setSelectedInvoice(null);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setSelectedInvoice(null);
  };

  const getStatusIcon = (invoice) => {
    if (invoice.status === 'cancelled') {
      return <FaTimesCircle className="text-red-500" title="Cancelled" />;
    } else if (invoice.downloaded) {
      return <FaCheck className="text-green-500" title="Downloaded" />;
    }
    return null;
  };

  // Helper to format bytes to KB/MB
  const formatFileSize = (bytes) => {
    if (!bytes || bytes <= 0) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const getStatusBadge = (invoice) => {
    if (invoice.status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
          <FaTimesCircle className="w-3 h-3" />
          Cancelled
        </span>
      );
    } else if (invoice.downloaded) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          <FaCheck className="w-3 h-3" />
          Downloaded
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
        Available
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={InvoiceIcon}
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
      />

      {/* Information Note */}
      {pageMeta.noteText && (
      <div
        className="relative overflow-hidden rounded-2xl px-4 py-2 shadow-lg opacity-0 animate-fade-in-up"
        style={{
          background: `linear-gradient(135deg, var(--primary-600), var(--primary-700))`,
          animationDelay: '100ms',
          animationFillMode: 'forwards',
        }}
      >
        <div className="relative flex items-center gap-3 text-white">
          <span className="text-2xl shrink-0">📄</span>
          <marquee
            className="text-sm md:text-base font-medium"
            behavior="scroll"
            direction="left"
            scrollamount="5"
          >
            {pageMeta.noteText}
          </marquee>
        </div>
      </div>
      )}

      {/* Invoice Content */}
      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : (
        <>
          {hasInvoiceFolderNav && (
            <div className="space-y-4">
              {(folderPath.length > 0 || currentFolder) && (
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={handleInvoiceBack}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 font-medium text-sm hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <div className="flex items-center gap-1 text-sm">
                    <button
                      type="button"
                      onClick={() => navigateToFolder(-1)}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                    >
                      <FiHome className="w-4 h-4" />
                      <span>Home</span>
                    </button>
                    {folderPath.map((folder, index) => (
                      <div key={folder._id} className="flex items-center">
                        <span className="text-gray-400 dark:text-gray-500 mx-1">/</span>
                        <button
                          type="button"
                          onClick={() => navigateToFolder(index)}
                          className="px-2 py-1 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                        >
                          {folder.name}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {getInvoiceFolderContents().length > 0 && (
                <div className="mb-4 sm:mb-6">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <FiFolder className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500" />
                    <h3 className="section-title">Folders</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                    {getInvoiceFolderContents().map((folder) => (
                      <div
                        key={folder._id}
                        onClick={() => openFolder(folder)}
                        className="group cursor-pointer flex items-center gap-2 sm:gap-3 px-2.5 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl bg-white dark:bg-dark-800 border-2 border-gray-200 dark:border-dark-600 hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-md sm:hover:shadow-lg transition-all duration-200 w-full sm:w-[180px] sm:min-h-[72px]"
                      >
                        <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-md sm:rounded-lg border border-primary-200 dark:border-primary-800 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 flex items-center justify-center sm:group-hover:scale-110 transition-transform duration-200">
                          <FiFolder className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <span className="text-xs sm:text-sm font-semibold text-dark-700 dark:text-gray-300 line-clamp-2 leading-snug min-w-0">
                          {folder.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        <div className="card overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
                  <th className="px-6 py-4 text-left table-th">Invoice Name</th>
                  <th className="px-6 py-4 text-left table-th">Size</th>
                  <th className="px-6 py-4 text-left table-th">Date & Time</th>
                  <th className="px-6 py-4 text-left table-th">Status</th>
                  <th className="px-6 py-4 text-right table-th">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                {filteredInvoices.length > 0 ? (
                  filteredInvoices.map((invoice, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <FaFilePdf className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-dark-800 dark:text-white truncate max-w-xs">
                            {invoice.originalName || `Invoice ${index + 1}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatFileSize(invoice.size)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(invoice.date).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(invoice)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {invoice.status !== 'cancelled' ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleDownload(invoice)}
                              disabled={!invoice.fileUrl}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FaDownload className="w-4 h-4" />
                              Download
                            </button>
                            {!invoice.fileUrl && (
                              <button
                                onClick={() => handleCancelInvoice(invoice)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                              >
                                <FaTrash className="w-3.5 h-3.5" />
                                Cancel
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">Invoice cancelled</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState
                        icon={FaFilePdf}
                        title="No invoices available"
                        containerClassName="py-12 text-center"
                        iconClassName="w-12 h-12 text-gray-300 dark:text-dark-600 mx-auto mb-3"
                        titleClassName="text-gray-500 dark:text-gray-400 text-sm font-normal"
                      />
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
                  <td colSpan={5} className="px-6 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total: {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-4 space-y-3">
            {filteredInvoices.length > 0 ? (
              filteredInvoices.map((invoice, index) => (
                <div key={index} className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                  <div className="flex items-start gap-3 mb-3">
                    <FaFilePdf className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-dark-800 dark:text-white truncate">
                        {invoice.originalName || `Invoice ${index + 1}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(invoice.date).toLocaleString()}
                      </p>
                    </div>
                    {getStatusBadge(invoice)}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <span>Size: {formatFileSize(invoice.size)}</span>
                  </div>
                  {invoice.status !== 'cancelled' ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(invoice)}
                        disabled={!invoice.fileUrl}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <FaDownload className="w-4 h-4" />
                        Download
                      </button>
                      {!invoice.fileUrl && (
                        <button
                          onClick={() => handleCancelInvoice(invoice)}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-red-500">Invoice cancelled</p>
                  )}
                </div>
              ))
            ) : (
              <EmptyState
                icon={FaFilePdf}
                title="No invoices available"
                containerClassName="p-8 text-center"
                iconClassName="w-12 h-12 text-gray-300 dark:text-dark-600 mx-auto mb-3"
                titleClassName="text-gray-500 dark:text-gray-400 text-sm font-normal"
              />
            )}
            <p className="text-right text-sm text-gray-500 dark:text-gray-400 pt-2">
              Total: {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        </>
      )}

      {/* Cancel Invoice Modal */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md animate-scale-in">
            <div className="p-6 border-b border-gray-100 dark:border-dark-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <FaExclamationTriangle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-dark-800 dark:text-white">Cancel Invoice</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to cancel this invoice?
              </p>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-dark-700 dark:text-gray-300">
                  Reason for cancellation (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason..."
                  rows={3}
                  className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex gap-3">
              <button
                onClick={closeCancelDialog}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              >
                Back
              </button>
              <button
                onClick={confirmCancelInvoice}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;